import { LinkOutlined } from '@ant-design/icons'
import { Box, Typography as MuiTypography } from '@mui/material'
import { Alert, Button, Table, TableColumnsType, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'

import { ONLY_DATE_FORMAT } from '../../../../../constants/global'
import { ITikTokAccount } from '../../../../../types/tiktok'
import { numberFormat } from '../../../../../utils'
import { ISkuLinkTarget } from '../../../../../utils/platform-link.util'
import { IUnlinkedSku } from '../types'
import SkuLinkModal from './SkuLinkModal'

interface Props {
  items: IUnlinkedSku[]
  isLoading: boolean
  error: boolean
  tiktokAccounts: ITikTokAccount[]
}

// SKU ของ TikTok ที่ยังหาสินค้าในระบบไม่เจอ — ร้านจะรู้ว่าต้องผูกตัวไหนก่อนโดยไม่ต้องเปิดดูทีละคำสั่งซื้อ
const UnlinkedSkusTable = ({
  items,
  isLoading,
  error,
  tiktokAccounts,
}: Props) => {
  const shopNameById = new Map(
    tiktokAccounts.map((account) => [account.shopId, account.sellerName]),
  )
  const [linkRow, setLinkRow] = useState<IUnlinkedSku | null>(null)
  // ผูกแล้วแต่แถวยังอยู่จนกว่าจะเติมต้นทุนย้อนหลัง — จำไว้ว่าผูกกับอะไรเพื่อบอกผู้ใช้
  const [linkedTo, setLinkedTo] = useState<Map<string, string>>(new Map())

  const rowKey = (record: IUnlinkedSku) =>
    `${record.shop_id}::${record.sku_id}::${record.seller_sku}`

  // ต้องเป็น object เดิมตลอดที่เปิดอยู่ — หน้าต่างผูกล้างค่าที่เลือกเมื่อ target เปลี่ยน
  const linkTarget = useMemo<ISkuLinkTarget | null>(
    () =>
      linkRow && {
        shopId: linkRow.shop_id,
        shopName: shopNameById.get(linkRow.shop_id),
        productId: linkRow.product_id,
        productName: linkRow.product_name,
        skuId: linkRow.sku_id,
        sku: linkRow.seller_sku,
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [linkRow],
  )

  const columns: TableColumnsType<IUnlinkedSku> = [
    {
      title: 'ลำดับ',
      key: 'index',
      align: 'right',
      width: 64,
      render: (_: unknown, __: IUnlinkedSku, index: number) => index + 1,
    },
    {
      title: 'สินค้าใน TikTok',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 320,
      ellipsis: { showTitle: true },
    },
    {
      title: 'SKU',
      dataIndex: 'seller_sku',
      key: 'seller_sku',
      // SKU บางตัวมีช่องว่างซ้อน (เช่น "รังนก  (CG)") ต้องเห็นตามจริงถึงจะผูกได้ตรง
      render: (value: string) =>
        value ? (
          <Typography.Text copyable style={{ whiteSpace: 'pre' }}>
            {value}
          </Typography.Text>
        ) : (
          '-'
        ),
    },
    {
      title: 'SKU ID',
      dataIndex: 'sku_id',
      key: 'sku_id',
      render: (value: string) =>
        value ? <Typography.Text copyable>{value}</Typography.Text> : '-',
    },
    {
      title: 'ร้าน',
      dataIndex: 'shop_id',
      key: 'shop_id',
      render: (value: string) => shopNameById.get(value) ?? value,
    },
    {
      title: 'คำสั่งซื้อ',
      dataIndex: 'order_count',
      key: 'order_count',
      align: 'right',
      width: 100,
      render: (value: number) => numberFormat(value, 0),
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 100,
      render: (value: number) => numberFormat(value, 0),
    },
    {
      title: 'สั่งล่าสุด',
      dataIndex: 'last_order_date',
      key: 'last_order_date',
      width: 110,
      render: (value: string) =>
        value ? dayjs(value).format(ONLY_DATE_FORMAT) : '-',
    },
    {
      title: 'ผูกสินค้า',
      key: 'link',
      width: 140,
      render: (_: unknown, record: IUnlinkedSku) => {
        const linkedName = linkedTo.get(rowKey(record))
        if (linkedName) {
          return <Tag color="green">ผูกกับ {linkedName} แล้ว</Tag>
        }
        return (
          <Button
            size="small"
            icon={<LinkOutlined />}
            onClick={() => setLinkRow(record)}
          >
            ผูกสินค้า
          </Button>
        )
      },
    },
  ]

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ mb: 1 }}>
        <MuiTypography variant="subtitle1" fontWeight={600}>
          SKU ที่ยังไม่ผูก
        </MuiTypography>
        <MuiTypography variant="caption" color="text.secondary">
          เรียงจากจำนวนคำสั่งซื้อที่ยังไม่มีต้นทุน
        </MuiTypography>
      </Box>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="วิธีผูก"
        description={
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              กด <b>ผูกสินค้า</b> ท้ายแถว → ค้นหาและเลือกสินค้าในระบบ → กด
              &quot;ผูก&quot;
            </li>
            <li>ผูกครบแล้ว กด &quot;เติมต้นทุนย้อนหลัง&quot;</li>
          </ol>
        }
      />
      {error && (
        <Alert
          type="error"
          message="ไม่สามารถโหลดรายการ SKU ที่ยังไม่ผูกได้ กรุณาลองใหม่อีกครั้ง"
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        rowKey={rowKey}
        columns={columns}
        dataSource={items}
        loading={isLoading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content', y: 320 }}
      />
      <SkuLinkModal
        open={!!linkRow}
        target={linkTarget}
        onClose={() => setLinkRow(null)}
        onLinked={(product) => {
          if (linkRow) {
            const key = rowKey(linkRow)
            setLinkedTo((prev) => new Map(prev).set(key, product.code))
          }
        }}
      />
    </Box>
  )
}

export default UnlinkedSkusTable
